import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  ContentChild,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  GlameraAnimationMode,
  GlameraIconDefinition,
  GlameraIconPosition,
  GlameraIconSize,
} from '../types';
import { coerceBooleanAttribute, coerceDurationMs, coerceIconSize, GLAMERA_DEFAULT_DURATION } from '../utils';

@Directive()
export abstract class GlameraIconBase implements OnDestroy, AfterContentChecked, AfterViewInit {
  @Input() animation: GlameraAnimationMode = 'hover';
  @Input() size: GlameraIconSize = 24;
  @Input() width?: GlameraIconSize | null;
  @Input() height?: GlameraIconSize | null;
  @Input() gap?: GlameraIconSize | null;
  @Input() iconPosition: GlameraIconPosition = 'start';
  @Input() duration: number | string = GLAMERA_DEFAULT_DURATION;
  @Input() delay: number | string = 0;
  @Input() color?: string | null;
  @Input() label?: string | null;
  @Input('aria-label') ariaLabel?: string | null;

  @Input()
  get playOnInit(): boolean {
    return this._playOnInit;
  }
  set playOnInit(value: boolean | string | null | undefined) {
    this._playOnInit = coerceBooleanAttribute(value);
  }

  @Input()
  get parentHover(): boolean {
    return this._parentHover;
  }
  set parentHover(value: boolean | string | null | undefined) {
    this._parentHover = coerceBooleanAttribute(value);
  }

  @Input()
  get decorative(): boolean {
    return this._decorative;
  }
  set decorative(value: boolean | string | null | undefined) {
    this._decorative = coerceBooleanAttribute(value);
  }

  isActive = false;
  isInitialPlay = false;
  hasProjectedContent = false;
  hostHovered = false;

  @HostBinding('class.glm-icon-host')
  readonly glmIconHostClass = true;

  @HostBinding('class.glm-host-hovered')
  get glmHostHoveredClass(): boolean {
    return this.hostHovered;
  }

  @ContentChild('glmLabel', { read: ElementRef })
  private labelRef?: ElementRef<HTMLElement>;

  private _decorative = false;
  private _parentHover = false;
  private _playOnInit = false;
  private initialPlayStarted = false;
  private clickTimer: ReturnType<typeof setTimeout> | null = null;
  private initialPlayTimer: ReturnType<typeof setTimeout> | null = null;

  protected constructor(
    protected readonly sanitizer: DomSanitizer,
    protected readonly cdr: ChangeDetectorRef
  ) {}

  abstract get iconDef(): GlameraIconDefinition | undefined;

  get iconName(): string | null {
    const def = this.iconDef;
    return def ? def.name : null;
  }

  get resolvedSize(): string {
    return coerceIconSize(this.size);
  }

  get resolvedWidth(): string {
    return coerceIconSize(this.width ?? this.size);
  }

  get resolvedHeight(): string {
    return coerceIconSize(this.height ?? this.size);
  }

  get resolvedGap(): string {
    return this.gap == null || this.gap === '' ? '0' : coerceIconSize(this.gap);
  }

  get resolvedDuration(): string {
    return `${coerceDurationMs(this.duration)}ms`;
  }

  get resolvedDelay(): string {
    return `${coerceDurationMs(this.delay)}ms`;
  }

  get a11yLabel(): string | null {
    if (this.hasProjectedContent || this.decorative) {
      return null;
    }
    return this.label || this.ariaLabel || null;
  }

  get a11yHidden(): string | null {
    if (this.hasProjectedContent) {
      return null;
    }
    return this.decorative || !this.a11yLabel ? 'true' : null;
  }

  get a11yRole(): string | null {
    if (this.hasProjectedContent) {
      return null;
    }
    return this.a11yLabel && !this.decorative ? 'img' : 'presentation';
  }

  get glyphAriaHidden(): string | null {
    return this.hasProjectedContent ? 'true' : null;
  }

  get safeSvg(): SafeHtml {
    const def = this.iconDef;
    if (!def) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const strokeWidth = def.strokeWidth ?? 2;
    return this.sanitizer.bypassSecurityTrustHtml(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${def.viewBox}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" focusable="false" aria-hidden="true">${def.svg}</svg>`
    );
  }

  @HostListener('mouseenter')
  onHostMouseEnter(): void {
    this.hostHovered = true;
  }

  @HostListener('mouseleave')
  onHostMouseLeave(): void {
    this.hostHovered = false;
  }

  onClick(): void {
    if (this.animation !== 'click') {
      return;
    }

    this.isActive = false;
    this.cdr.markForCheck();

    if (this.clickTimer !== null) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }

    this.clickTimer = setTimeout(() => {
      this.isActive = true;
      this.cdr.markForCheck();

      const total = coerceDurationMs(this.duration) + coerceDurationMs(this.delay) + 40;
      this.clickTimer = setTimeout(() => {
        this.isActive = false;
        this.clickTimer = null;
        this.cdr.markForCheck();
      }, total);
    }, 0);
  }

  ngAfterViewInit(): void {
    if (this._playOnInit) {
      this.triggerInitialPlay();
    }
  }

  ngAfterContentChecked(): void {
    const hasContent = (this.labelRef?.nativeElement.childNodes.length ?? 0) > 0;
    if (hasContent !== this.hasProjectedContent) {
      this.hasProjectedContent = hasContent;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    if (this.clickTimer !== null) {
      clearTimeout(this.clickTimer);
    }
    if (this.initialPlayTimer !== null) {
      clearTimeout(this.initialPlayTimer);
    }
  }

  private triggerInitialPlay(): void {
    if (this.initialPlayStarted || this.animation === 'none' || this.animation === 'loop') {
      return;
    }

    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    this.initialPlayStarted = true;
    this.isInitialPlay = false;
    this.cdr.markForCheck();

    this.initialPlayTimer = setTimeout(() => {
      this.isInitialPlay = true;
      this.cdr.markForCheck();

      const total = coerceDurationMs(this.duration) + coerceDurationMs(this.delay) + 40;
      this.initialPlayTimer = setTimeout(() => {
        this.isInitialPlay = false;
        this.initialPlayTimer = null;
        this.cdr.markForCheck();
      }, total);
    }, 0);
  }
}
